import Whatsapp from 'assets/svg/whatsapp/whatsapp.svg'
import Flex from 'common/components/Flex'
import Header from 'common/components/Header/BusinessSolutionHeader'
import Layout from 'common/components/Layout'
import Typography from 'common/components/Typography'
import { CONTACTS, WHATSAPP_URL } from 'common/constants'
import theme from 'common/theme'
import cdn from 'lib/cdn'
import MediaHelper from 'lib/media-helper'
import transformData from 'lib/transform/array-to-camel-case'
import dynamic from 'next/dynamic'
import { ReactNode, useEffect, useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import ContactUsForm from 'services/BusinessSolution/components/ContactUsForm'
import Email from 'services/Services/assets/svg/email.svg'
import Location from 'services/Services/assets/svg/location.svg'
import Phone from 'services/Services/assets/svg/phone.svg'
import { useStoreActions, useStoreState } from 'stores/hooks'
import styled from 'styled-components'

const BackgroundImage = cdn({
  path: '/assets/images/helloRalali/contact-us-banner.png',
})

const HeaderContainer = styled.div`
  width: 100%;
  /* Targets <Header/>  */
  > div {
    position: relative;
    width: 100%;
    height: 370px;
    padding-top: 90px;
    box-sizing: border-box;
    background: url(${BackgroundImage});
    display: flex;
    justify-content: center;
    align-items: center;
  }
`

const BannerContentContainer = styled(Flex)`
  gap: 12px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 0 24px;

  h1,
  h4 {
    color: ${theme.colors.white};
  }
`

const FloatingContent = styled(Flex)`
  width: 100%;
  position: relative;
`

const FormSection = styled.section`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  align-items: center;
  justify-content: center;
  width: ${theme.layout.containerWidth};
  background-color: ${theme.colors.white};
  box-shadow: 0 8px 11px 0 rgba(0, 0, 0, 0.05);
  border-radius: 16px;
  transform: translate(0, -50px);

  ${MediaHelper('desktop')`
    grid-template-columns: repeat(1, 1fr);
    width: 100%;
    transform: unset;
    border-radius: 0;
    box-shadow: unset;
  `}
`

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 27px 50px 36px;
  box-sizing: border-box;

  > p {
    line-height: normal;
    font-size: 24px;
    align-self: flex-start;
    margin-bottom: 32px;
  }

  div {
    label {
      font-size: 14px;
      color: ${theme.colors.grayDark};
    }
  }

  button {
    width: 100%;
  }

  ${MediaHelper('laptop')`padding: 27px 24px 36px;`}
`

const AddressContainer = styled(Flex)`
  background-color: ${theme.colors.grayLighter};
  border-radius: 16px 0 0 16px;
  height: 100%;
  padding: 27px 50px 36px;
  box-sizing: border-box;

  ${MediaHelper('desktop')`
    border-radius: 0;
  `}

  ${MediaHelper('laptop')`
    padding: 27px 24px 36px;
  `}
`

const AddressInfoContainer = styled(Flex)`
  gap: 14px;
  margin-bottom: 16px;
  align-items: center;

  > svg {
    flex-shrink: 0;
    position: relative;
  }
`

const MapSection = styled.section`
  margin-top: 80px;
  margin-bottom: 100px;
  display: flex;
  align-items: center;
  flex-direction: column;
  width: 976px;
  height: 420px;
  box-sizing: border-box;
  font-size: 40px;
  font-weight: bold;

  ${MediaHelper('laptop')`
      margin-top: 20px;
      width: 100%;
      height: 320px;
      padding: 0 24px;
      ${theme.typography.bh2};
  `}

  iframe {
    margin-top: 40px;
    border: 0;

    ${MediaHelper('desktop')`
      width: inherit;
  `}
  }
`

const Link = styled.a`
  color: ${theme.colors.orange};
`

interface IAddress {
  lang: string
  title: string
  location?: ReactNode
  description?: ReactNode
  phone?: string
  email?: string | ReactNode
  whatsapp?: ReactNode
}

/* zaky-syihab: a fix for broken <a /> styling, beside that we can't use styled component to create FooterWrapper */
const LandingFooter = dynamic(
  () => import('common/components/LandingFooter'),
  { ssr: false },
)

const ADDRESS: IAddress[] = [
  {
    lang: 'id',
    title: 'Kantor Ralali',
    location: (
      <Typography>
        <>
          Capital Cove Business Loft, BSD City,
          <br />
          Jl. BSD Grand Boulevard No.26, Cilenggang, Kecamatan
          Serpong, Kota Tangerang Selatan, Banten 15310
        </>
      </Typography>
    ),
    whatsapp: <Link href={WHATSAPP_URL}>{CONTACTS.PHONE}</Link>,
    email: (
      <Link href={`mailto:${CONTACTS.EMAIL}`}>{CONTACTS.EMAIL}</Link>
    ),
  },
  {
    lang: 'id',
    title: 'Ralali Center',
    location: (
      <Typography>
        <>
          Lindeteves Trade Center Lt.UG Blok A23 No.6
          <br />
          Jl. Hayam Wuruk 127, Jakarta Barat 11180 - Indonesia
        </>
      </Typography>
    ),
    phone: '021 - 6230 8786',
  },
  {
    lang: 'id',
    title: 'Layanan Pengaduan Konsumen',
    description: (
      <Typography>
        Sebagai bagian dari kepatuhan terhadap tata kelola perusahaan
        dalam melindungi konsumen, kami juga ingin membagikan kontak
        WhatsApp dari Direktorat Jenderal Perlindungan Konsumen dan
        Tertib Niaga Kementerian Perdagangan Republik Indonesia.
      </Typography>
    ),
    whatsapp: (
      <Link href="https://wa.me/+6285311111010">0853-1111-1010</Link>
    ),
  },
  {
    lang: 'en',
    title: 'Ralali Office',
    location: (
      <Typography>
        <>
          Capital Cove Business Loft, BSD City,
          <br />
          Jl. BSD Grand Boulevard No.26, Cilenggang, Serpong
          Sub-District, South Tangerang City, Banten 15310
        </>
      </Typography>
    ),
    whatsapp: <Link href={WHATSAPP_URL}>{CONTACTS.PHONE}</Link>,
    email: (
      <Link href="mailto:hello@ralali.com">hello@ralali.com</Link>
    ),
  },
  {
    lang: 'en',
    title: 'Ralali Center',
    location: (
      <Typography>
        <>
          Lindeteves Trade Center 2nd Floor Block A23 No.6
          <br />
          Jl. Hayam Wuruk 127, West Jakarta 11180 - Indonesia
        </>
      </Typography>
    ),
    phone: '021 - 6230 8786',
  },
  {
    lang: 'en',
    title: 'Consumer Complaint Service',
    description: (
      <Typography>
        As part of our compliance with corporate governance in
        consumer protection, we would also like to share the WhatsApp
        contact of the Directorate General of Consumer Protection and
        Business Order of the Ministry of Trade of the Republic of
        Indonesia.
      </Typography>
    ),
    whatsapp: (
      <Link href="https://wa.me/+6285311111010">0853-1111-1010</Link>
    ),
  },
]

function Address({ lang }) {
  const filterAddress = ADDRESS.filter(
    (context) => context.lang === lang,
  )
  return (
    <>
      {filterAddress.map((address) => (
        <Flex
          key={address.title}
          flexDirection="column"
          margin="0 0 11px 0"
        >
          <Typography type="bh6" marginBottom={16}>
            {address.title}
          </Typography>
          {address.location && (
            <AddressInfoContainer>
              <Location />
              {address.location}
            </AddressInfoContainer>
          )}
          {address.description && (
            <AddressInfoContainer>
              {address.description}
            </AddressInfoContainer>
          )}
          {address.phone && (
            <AddressInfoContainer>
              <Phone />
              <Typography>
                <>{address.phone}</>
              </Typography>
            </AddressInfoContainer>
          )}
          {address.whatsapp && (
            <AddressInfoContainer>
              <Whatsapp width="12px" />
              <Typography>
                <>{address.whatsapp}</>
              </Typography>
            </AddressInfoContainer>
          )}
          {address.email && (
            <AddressInfoContainer>
              <Email />
              <Typography>
                <>{address.email}</>
              </Typography>
            </AddressInfoContainer>
          )}
        </Flex>
      ))}
    </>
  )
}

function ContactUsPage({ lang }): JSX.Element {
  const { t } = useTranslation('home')
  const { businessSolution } = useStoreActions((actions) => actions)
  const businessSolutionState = useStoreState(
    (state) => state.businessSolution,
  )

  useEffect(() => {
    const params = { page_code: 'business_solution' }
    businessSolution.getData(params)
  }, [])

  const RenderHeader = useMemo(() => {
    const result = businessSolutionState.data?.sections.find(
      (section) => section.group === 'menu-header',
    )
    if (!result) return null
    return (
      <Header
        position="fixed"
        isNotTransparent
        lang={lang}
        menus={transformData(result.data.items)}
      />
    )
  }, [businessSolutionState.data?.sections])

  return (
    <Layout
      seo={{
        title: 'Hubungi Kami | Ralali.com',
        additionalMetaTags: [
          {
            name: 'keywords',
            content: 'hubungi ralali',
          },
        ],
      }}
      header={{
        component: RenderHeader,
      }}
      responsive
      fluid
    >
      <Flex flexDirection="column" alignItems="center">
        <HeaderContainer>
          <Flex flexDirection="column">
            <BannerContentContainer data-testid="contact-us-banner">
              <Typography type="bh1" fontSize={40} textAlign="center">
                {t('CONTACT_US')}
              </Typography>
              <Typography
                type="rh4"
                textAlign="center"
                marginBottom={50}
              >
                <Trans>{t('CONTACT_US_SUBTITLE')}</Trans>
              </Typography>
            </BannerContentContainer>
          </Flex>
        </HeaderContainer>
        <FloatingContent flexDirection="column" alignItems="center">
          <FormSection>
            <AddressContainer
              flexDirection="column"
              alignItems="flex-start"
              data-testid="contact-us-address"
            >
              <Typography type="bh2" marginBottom={32}>
                {t('COMPANY_INFORMATION')}
              </Typography>
              <Address lang={lang} />
            </AddressContainer>
            <FormContainer data-testid="contact-us-form">
              <ContactUsForm inputWidth="100%" />
            </FormContainer>
          </FormSection>
          <MapSection>
            {t('FIND_US')}
            <iframe
              data-testid="ralali-map"
              title="Kantor Ralali"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3965.720378304066!2d106.65774617499106!3d-6.300425493688712!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x2e69f7d4254bc1d3%3A0x875c32924a13c98c!2sRalali%20Headquarter!5e0!3m2!1sen!2sid!4v1745810047371!5m2!1sen!2sid"
              width="100%"
              height="100%"
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </MapSection>
          <LandingFooter />
        </FloatingContent>
      </Flex>
    </Layout>
  )
}

export default ContactUsPage
